import { Theme, useTheme } from '@mui/material/styles';
import { t } from 'i18next';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { JobEtaBasic } from '~hooks/useJob/models';
import { Nullable } from '~types/Nullable';
import useCurrentTime from '~utils/hooks/useCurrentTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import {
  EtaLeg,
  etaLegForState,
  EtaTone,
  etaTone,
  formatCompactDuration,
  isEnRouteEtaState,
} from './etaFormat';
import useJobEtaWatch from './useJobEtaWatch';

interface EtaCellProps {
  eta: Nullable<JobEtaBasic>;
  jobId: string;
}

function toneColor(theme: Theme, tone: EtaTone) {
  if (tone === 'critical') return theme.semantic.rowCriticalText;
  if (tone === 'warning') return theme.semantic.rowWarningText;
  if (tone === 'muted') return theme.brandV2.colors.treadGray3;

  return theme.brandV2.colors.treadBlack;
}

function legLabel(leg: EtaLeg) {
  return leg === 'pickup' ? t('dispatch.eta.leg_pickup') : t('dispatch.eta.leg_dropoff');
}

/**
 * The live ETA line in the dispatch Cycle column: a count-up while the truck sits on
 * site, a count-down while it is en route.
 *
 * Two gates, both required. `live_eta_enabled` is the per-company kill switch: with the
 * flag off nothing renders, however much ETA the backend keeps pushing for jobs already
 * opted in. The presence of `eta` is the per-row gate.
 */
export default function EtaCell({ eta, jobId }: EtaCellProps) {
  const theme = useTheme();
  const isLiveEtaEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.liveEtaEnabled,
  });

  const dwellStartedAt = eta?.idleAt ?? null;
  const arrivesAt = eta?.etaAt ?? null;
  const hasRunningTimer = isLiveEtaEnabled && Boolean(dwellStartedAt || arrivesAt);
  const now = useCurrentTime(hasRunningTimer);

  useJobEtaWatch(jobId, Boolean(isLiveEtaEnabled && eta && isEnRouteEtaState(eta.state)));

  if (!isLiveEtaEnabled || !eta) return null;

  const leg = etaLegForState(eta.state);
  const tone = etaTone(eta, now);

  if (!leg || !tone) return null;

  if (dwellStartedAt) {
    const elapsedSeconds = now.diff(dwellStartedAt, 'second');

    return (
      <OverflowAwareText sx={{ color: toneColor(theme, tone) }}>
        {t('dispatch.eta.on_site', {
          duration: formatCompactDuration(elapsedSeconds),
          leg: legLabel(leg),
        })}
      </OverflowAwareText>
    );
  }

  if (arrivesAt) {
    const remainingSeconds = arrivesAt.diff(now, 'second');
    // A refresh can always land later than the prediction it replaces, so past `eta_at`
    // the count-down flips to counting up how far behind the prediction the truck now is.
    const isRunningBehind = remainingSeconds < 0;
    const countdownText = isRunningBehind
      ? t('dispatch.eta.overdue', {
          duration: formatCompactDuration(-remainingSeconds),
          leg: legLabel(leg),
        })
      : t('dispatch.eta.en_route', {
          duration: formatCompactDuration(remainingSeconds),
          leg: legLabel(leg),
        });

    return (
      <OverflowAwareText sx={{ color: toneColor(theme, tone) }}>
        {countdownText}
      </OverflowAwareText>
    );
  }

  // En route with no arrival: the routing call either has not landed yet or bailed (no
  // driver location, no site coordinates). Labeled rather than blank, so it cannot be
  // mistaken for a job that is not tracked at all.
  return (
    <OverflowAwareText sx={{ color: toneColor(theme, tone) }}>
      {t('dispatch.eta.unavailable', { leg: legLabel(leg) })}
    </OverflowAwareText>
  );
}
